import { makeAutoObservable, runInAction } from 'mobx'
import { getCourseGroupsAndSubGroups } from 'api/shortlister'
import { formatLabelWithEmoji } from 'shared/utils/formatLabelWithEmoji'

/**
 * Catalog of categories → subcategories (student AI Tools courseMap).
 * Independent of researchSessionStore.
 */
class CourseMapStore {
	courseMap = {}
	loadedDegree = null
	loading = false
	error = null

	_inflightDegree = null
	_inflight = null

	constructor() {
		makeAutoObservable(
			this,
			{
				_inflightDegree: false,
				_inflight: false,
			},
			{ autoBind: true },
		)
	}

	/**
	 * Same transform as student fetchCourseMap.
	 */
	async fetchCourseMap(degree) {
		const normalizedDegree = degree ?? null
		this.loading = true
		this.error = null

		try {
			const response = await getCourseGroupsAndSubGroups(normalizedDegree)
			const dataCatAndSubcat = response?.data

			const newCategoriesMap = {}
			if (dataCatAndSubcat && typeof dataCatAndSubcat === 'object') {
				for (const categoryTitle of Object.keys(dataCatAndSubcat)) {
					const categoryEmoji = dataCatAndSubcat[categoryTitle]?.emoji
					const titleWithEmoji = formatLabelWithEmoji(categoryEmoji, categoryTitle)
					const subcategoryList =
						dataCatAndSubcat[categoryTitle]?.subcategories || []
					const subCatWithEmoji = subcategoryList
						.map(group => formatLabelWithEmoji(group?.emoji, group?.name))
						.filter(Boolean)
					if (titleWithEmoji) {
						newCategoriesMap[titleWithEmoji] = subCatWithEmoji
					}
				}
			}

			runInAction(() => {
				this.courseMap = newCategoriesMap
				this.loadedDegree = normalizedDegree
				this.error = null
			})
			return newCategoriesMap
		} catch (error) {
			runInAction(() => {
				this.error = error
			})
			throw error
		} finally {
			runInAction(() => {
				this.loading = false
			})
		}
	}

	/**
	 * Mirror student useCourseMap: skip if map loaded for same degree.
	 * Pass `{ force: true }` to refetch (Retry).
	 */
	ensureLoaded(degree, { force = false } = {}) {
		const normalizedDegree = degree ?? null
		const isEmpty = Object.keys(this.courseMap || {}).length === 0
		const isDegreeMismatch = this.loadedDegree !== normalizedDegree

		if (!force && !isEmpty && !isDegreeMismatch && !this.error) {
			return Promise.resolve(this.courseMap)
		}

		if (
			!force &&
			this.loading &&
			this._inflight &&
			this._inflightDegree === normalizedDegree
		) {
			return this._inflight
		}

		this._inflightDegree = normalizedDegree
		this._inflight = this.fetchCourseMap(normalizedDegree).finally(() => {
			if (this._inflightDegree === normalizedDegree) {
				this._inflight = null
				this._inflightDegree = null
			}
		})
		return this._inflight
	}
}

export const courseMapStore = new CourseMapStore()
